/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/


import type { CbSelectOption } from "@app/widgets/base/select/select.component";

export const RGB_COLORS: string[] = [
	"rgb(102, 198, 28)",
	"rgb(22, 179, 100)",
	"rgb(21, 183, 158)",
	"rgb(6, 174, 212)",
	"rgb(11, 165, 236)",
	"rgb(46, 144, 250)",
	"rgb(41, 112, 255)",
	"rgb(97, 114, 243)",
	"rgb(135, 91, 247)",
	"rgb(122, 90, 248)",
	"rgb(212, 68, 241)",
	"rgb(238, 70, 188)",
	"rgb(246, 61, 104)",
	"rgb(255, 68, 5)",
	"rgb(239, 104, 32)",
	"rgb(234, 170, 8)",
	"rgb(78, 91, 166)",
	"rgb(102, 112, 133)",
];

const RGB_LABELS: string[] = [
	"COMMON.COLORS.MOSS",
	"COMMON.COLORS.LIGHT_GREEN",
	"COMMON.COLORS.GREEN",
	"COMMON.COLORS.TEAL",
	"COMMON.COLORS.CYAN",
	"COMMON.COLORS.LIGHT_BLUE",
	"COMMON.COLORS.BLUE",
	"COMMON.COLORS.DARK_BLUE",
	"COMMON.COLORS.INDIGO",
	"COMMON.COLORS.VIOLET",
	"COMMON.COLORS.PURPLE",
	"COMMON.COLORS.FUCHSIA",
	"COMMON.COLORS.PINK",
	"COMMON.COLORS.ROSE",
	"COMMON.COLORS.DARK_ORANGE",
	"COMMON.COLORS.ORANGE",
	"COMMON.COLORS.YELLOW",
	"COMMON.COLORS.RED",
];

export const RGB_OPTIONS: CbSelectOption[] = RGB_COLORS.map((color, index) => ({
	value: color,
	label: RGB_LABELS[index],
}));

export const lightenRGBColor = (
	rgbString: string,
	percentage: number,
): string => {
	// 1. Extract RGB Values:
	const match = rgbString.match(/\d+/g);
	if (!match || match.length !== 3) {
		return rgbString;
	}

	const [r, g, b] = match.map(Number);

	// 2. Calculate New Values:
	const amount = Math.round(255 * (percentage / 100));
	const newR = Math.min(r + amount, 255);
	const newG = Math.min(g + amount, 255);
	const newB = Math.min(b + amount, 255);

	// 3. Construct Result:
	return `rgb(${newR}, ${newG}, ${newB})`;
};

export const darkenRGBColor = (
	rgbString: string,
	percentage: number,
): string => {
	// 1. Extract RGB Values:
	const match = rgbString.match(/\d+/g);
	if (!match || match.length !== 3) {
		return rgbString;
	}

	const [r, g, b] = match.map(Number);

	// 2. Calculate New Values:
	const amount = Math.round(255 * (percentage / 100));
	const newR = Math.max(r - amount, 0);
	const newG = Math.max(g - amount, 0);
	const newB = Math.max(b - amount, 0);

	// 3. Construct Result:
	return `rgb(${newR}, ${newG}, ${newB})`;
};
